import { RefObject, useEffect } from 'react';

const defaultOptions: IntersectionObserverInit = {
    rootMargin: '0px',
    threshold: 1.0,
};

/**
 * Use this hook to fire an action when the element is seen
 * by the user (eg: they scrolled down to it)
 * @param ref
 * @param callback
 * @param options
 */
export function useVisibility(
    ref: RefObject<HTMLElement>,
    callback: (ready: boolean) => void,
    options = defaultOptions,
) {
    useEffect(() => {
        /**
         * Bail if the given ref cannot be accessed when this
         * hooks 'mounts'
         */
        if (!ref.current) {
            console.error('ref.current missing');
            return undefined;
        }

        /**
         * Setup the observer object
         */
        const observer = new IntersectionObserver(([entry], obs) => {
            /**
             * If we're not intersecting, that means the element
             * is not 'in view' based on the provided thresholds etc
             */
            if (!entry.isIntersecting) {
                return;
            }

            /**
             * If we get here the element is within the threshhold,
             * so to prevent it firing multiple times we need to `unobserve`
             */
            if (ref.current) {
                obs.unobserve(ref.current);
            }

            /**
             * Finally call the user callback (only once)
             */
            callback(true);
        }, options);

        /**
         * Start listening
         */
        observer.observe(ref.current);

        return () => {
            /**
             * Stop listening if this element unmounts
             */
            observer.disconnect();
        };
    }, [ref, callback, options]);
}
